import React from 'react';

import DollarIcon from "images/dashboard/dollar-icon.svg";
import ChartUpIcon from "images/dashboard/chart-up.svg";
import ChartDownIcon from "images/dashboard/chart-down.svg";
import { getValueByPath, formatValue } from '../Meta/metaUtils';

/**
 * Basic KPI/stat card used on the new dashboard.
 * - Left: circular icon holder (empty by default; user can pass an image src)
 * - Center: title and big value
 * - Right: percentage delta with tiny trend image placeholder and a comparison label
 */
export default function SimpleStatCard({
    title = '',
    statTitle = '',
    value = '',
    changePct = 0,
    isPositive,
    comparisonLabel = 'v/s last year',
    dataContext,
    ...props
}) {
    // Resolve display values from specs or primitives
    const resolveDisplay = (spec, defaultFormat = 'string') => {
        if (spec == null) return '';
        if (typeof spec === 'string' || typeof spec === 'number') {
            return formatValue(spec, defaultFormat);
        }
        if (spec && typeof spec === 'object') {
            const fallback = spec.hasOwnProperty('default') ? spec.default : undefined;
            const raw = spec.path ? getValueByPath(dataContext, spec.path) : undefined;

            if (raw == undefined || raw == null) {
                return fallback;
            }

            return formatValue(raw, spec.format || defaultFormat, {
                prefix: spec.prefix || '',
                suffix: spec.suffix || '',
                maximumFractionDigits: spec.maximumFractionDigits ?? 2,
            });
        }
        return '';
    };

    const resolveNumber = (spec, fallback = 0) => {
        if (typeof spec === 'number') return spec;
        if (typeof spec === 'string') {
            const n = Number(spec);
            return Number.isFinite(n) ? n : fallback;
        }
        if (spec && typeof spec === 'object') {
            const defaultVal = spec.hasOwnProperty('default') ? spec.default : undefined;
            const raw = spec.path ? getValueByPath(dataContext, spec.path) : undefined;
            const effective = (raw !== undefined && raw !== null) ? raw : defaultVal;
            const n = typeof effective === 'number' ? effective : Number(effective);
            return Number.isFinite(n) ? n : fallback;
        }
        return fallback;
    };

    const displayValue = resolveDisplay(value, 'string');
    const numericChange = resolveNumber(changePct, 0);
    const displayComparison = resolveDisplay(comparisonLabel, 'string');
    const positiveEffective = typeof isPositive === 'boolean' ? isPositive : (Number.isFinite(numericChange) ? numericChange >= 0 : true);

    const subtitleText = resolveDisplay(value?.subtitle ?? props?.subtitle, 'string');

    // Typography helpers
    const SIZE_MAP = {
        xs: 'text-xs',
        sm: 'text-sm',
        md: 'text-base',
        lg: 'text-lg',
        xl: 'text-xl',
        '2xl': 'text-2xl',
        '3xl': 'text-3xl',
        '4xl': 'text-4xl',
    };
    const WEIGHT_MAP = {
        thin: 'font-thin',
        extralight: 'font-extralight',
        light: 'font-light',
        normal: 'font-normal',
        medium: 'font-medium',
        semibold: 'font-semibold',
        bold: 'font-bold',
        extrabold: 'font-extrabold',
        black: 'font-black',
    };
    const buildTextClasses = (spec, defaults) => {
        const d = defaults || {};
        const sizeClass = spec?.size && SIZE_MAP[spec.size] ? SIZE_MAP[spec.size] : (d.defaultSizeClass || '');
        const weightClass = spec?.fontWeight && WEIGHT_MAP[spec.fontWeight] ? WEIGHT_MAP[spec.fontWeight] : (d.defaultWeightClass || '');
        const colorClass = typeof spec?.color === 'string' && spec.color.length ? spec.color : (d.defaultColorClass || '');
        const extra = typeof spec?.className === 'string' ? spec.className : '';
        return [sizeClass, weightClass, colorClass, d.extraClass || '', extra].filter(Boolean).join(' ');
    };

    // Value typography
    const valueVisible = value?.visible !== false;
    const valueTextClass = buildTextClasses(value, { defaultSizeClass: 'text-2xl', defaultWeightClass: 'font-semibold', defaultColorClass: '', extraClass: 'leading-8 tracking-tight' })
   
    

    // Delta typography; allow overrides via changePct spec
    const deltaVisible = changePct?.visible !== false;
    const deltaBaseColor = changePct?.color
        ? changePct.color
        : (positiveEffective
            ? (changePct?.positiveColor || 'text-green-600')
            : (changePct?.negativeColor || 'text-rose-600'));
    const deltaPrefix = positiveEffective ? '+' : '-';
    const deltaTextClass = buildTextClasses(changePct, { defaultSizeClass: 'text-xs', defaultWeightClass: 'font-semibold', defaultColorClass: deltaBaseColor });
    const TrendImage = positiveEffective ? ChartUpIcon : ChartDownIcon;

    // Comparison label typography
    const comparisonVisible = comparisonLabel?.visible === true;
    const comparisonTextClass = buildTextClasses(comparisonLabel, { defaultSizeClass: 'text-[11px]', defaultWeightClass: '', defaultColorClass: 'text-[#585858]' });

    return (
        <div className="flex items-center text-right gap-2">
            {valueVisible ? (
                <div className="flex flex-col font-semibold items-start">
                    <div className={valueTextClass}>
                        {displayValue}
                    </div>
                    {subtitleText ? (
                        <div className="mt-2 text-sm text-[#8a8a8a]">
                            {subtitleText}
                        </div>
                    ) : null}
                </div>
            ) : null}

            {(deltaVisible || comparisonVisible) ? (
                <div className="flex flex-col items-start">
                    {deltaVisible ? (
                        <div className={`flex flex-col gap-1 ${deltaTextClass}`}>
                            <span>
                                {deltaPrefix}
                                {Math.abs(Number(numericChange))}%
                            </span>
                            {TrendImage ? <img src={TrendImage} alt="" className="h-3 w-auto" /> : null}
                        </div>
                    ) : null}
                    {comparisonVisible ? (
                        <div className={comparisonTextClass}>
                            {displayComparison}
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
